import React, { useRef, useState, useEffect } from "react";
import { X, FileSpreadsheet, Loader2, Check, AlertTriangle, Repeat, Landmark, Trash2, Plus, Download } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const emptyRow = () => ({ date: "", sens: "", de_a: "", tenant: "", montant: "", type: "", property: "", description: "", status: "Payé" });

const norm = (s) => (s || "").toString().toLowerCase().trim();
const parseNum = (m) => parseFloat(String(m ?? "").replace(/[^\d.,-]/g, "").replace(",", ".")) ;
const sensFromAmount = (m) => { const n = parseNum(m); return isNaN(n) || n === 0 ? null : (n < 0 ? "Dépense" : "Revenu"); };

// Détection de catégorie par mots-clés du libellé (DE/A + description). Renvoie une catégorie existante.
const CATEGORY_HINTS = [
  { cats: ["Charge déductible : Assurance", "Assurance"], kw: ["assur", "axa", "maif", "macif", "matmut", "gmf", "allianz", "generali", "pno", "mma", "groupama"] },
  { cats: ["Charge déductible : Intérêts d'emprunt", "Intérêts d'emprunt"], kw: ["interet", "intérêt", "pret", "prêt", "credit", "crédit", "emprunt", "echeance", "échéance", "mensualit"] },
  { cats: ["Charge déductible : Taxe foncière", "Taxe foncière"], kw: ["taxe fonci", "impot fonci", "impôt fonci", "dgfip", "tresor public", "trésor public"] },
  { cats: ["Charge déductible : Provisions pour charges de copropriété", "Charges de copropriété"], kw: ["syndic", "copropri", "foncia", "citya", "nexity"] },
  { cats: ["Charge déductible : Travaux d'entretien et de réparation", "Entretien / Réparations", "Travaux"], kw: ["entretien", "reparation", "réparation", "plomb", "electricien", "électricien", "chauffagi", "serrur", "peinture", "travaux", "menuis", "vitrier"] },
  { cats: ["Charge déductible : Frais de gestion et d'administration", "Honoraires d'agence"], kw: ["honoraire", "gestion locative", "agence", "frais de gestion"] },
  { cats: ["Loyer"], kw: ["loyer", "location", "quittance"] },
  { cats: ["Charges / provisions"], kw: ["provision", "charges locatives", "regularisation", "régularisation"] },
  { cats: ["Dépôt de garantie perçu", "Dépôt de garantie"], kw: ["caution", "depot de garantie", "dépôt de garantie"] },
];
const makeCategoryGuesser = (cats = { expense: [], revenue: [] }) => {
  const all = [...(cats.revenue || []), ...(cats.expense || [])];
  const pick = (cands) => cands.find((c) => all.some((x) => norm(x) === norm(c))) || "";
  return (text) => {
    const q = norm(text);
    if (!q) return "";
    for (const h of CATEGORY_HINTS) {
      if (h.kw.some((k) => q.includes(k))) { const c = pick(h.cats); if (c) return c; }
    }
    return "";
  };
};
function makeGuesser(properties, savedMap = {}) {
  const idx = properties.map((p) => {
    const street = norm(p.address).split(",")[0];
    return { name: p.name, keys: [norm(p.tenant), norm(p.name), street].filter((x) => x && x.length >= 3) };
  });
  return (deA) => {
    const q = norm(deA);
    if (!q) return "";
    if (savedMap[q]) return savedMap[q];
    for (const p of idx) {
      for (const k of p.keys) {
        if (q === k || q.includes(k) || k.includes(q)) return p.name;
      }
    }
    return "";
  };
}

export default function ImportHistoryModal({ onClose, onImported, properties = [], cats = { expense: [], revenue: [] } }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(null);
  const [savedMap, setSavedMap] = useState({});
  const fileRef = useRef(null);
  const guessProp = makeGuesser(properties, savedMap);
  const guessCat = makeCategoryGuesser(cats);
  const autoFill = (arr) => arr.map((r) => ({ ...r, sens: r.sens || sensFromAmount(r.montant) || "Dépense", property: r.property || guessProp(r.tenant), type: r.type || guessCat(`${r.tenant || ""} ${r.description || ""}`) }));

  useEffect(() => {
    api.get("/finances/import-history/mappings").then((r) => setSavedMap(r.data.mappings || {})).catch(() => {});
  }, []);

  const rowSens = (r) => {
    const t = norm(r.sens);
    if (t.startsWith("rev")) return "Revenu";
    if (t.startsWith("dep") || t.startsWith("dép")) return "Dépense";
    return sensFromAmount(r.montant) || "Dépense";
  };
  const buildRecap = (rows) => rows.reduce((a, r) => {
    const n = parseNum(r.montant); const amt = isNaN(n) ? 0 : Math.abs(n);
    if (rowSens(r) === "Revenu") { a.nbRev += 1; a.totRev += amt; } else { a.nbDep += 1; a.totDep += amt; }
    return a;
  }, { nbRev: 0, totRev: 0, nbDep: 0, totDep: 0 });

  const onExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/finances/import-history/parse", fd);
      const parsed = autoFill((data.rows || []).map((r) => ({ ...emptyRow(), ...r, montant: r.montant ?? "" })));
      const clean = parsed.filter((r) => Object.values(r).some((v) => String(v ?? "").trim()))
        .map((r) => ({ ...r, montant: r.montant == null ? "" : String(r.montant) }));
      if (!clean.length) { toast.error("Aucune ligne exploitable dans le fichier"); setBusy(false); return; }
      setPending(clean);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fichier illisible");
    } finally { setBusy(false); }
  };

  const confirmImport = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const res = await api.post("/finances/import-history", { rows: pending });
      setResult(res.data);
      setPending(null);
      onImported?.();
      // Rafraîchit le cache des mappings pour repartir d'un nouvel import propre
      api.get("/finances/import-history/mappings").then((r) => setSavedMap(r.data.mappings || {})).catch(() => {});
      if (fileRef.current) fileRef.current.value = "";
      if ((res.data.created_count || 0) > 0) toast.success(`${res.data.created_count} écriture(s) importée(s)`);
      else toast.message("Aucune nouvelle écriture (doublons ou lignes incomplètes)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import impossible");
    } finally { setBusy(false); }
  };

  const recap = pending ? buildRecap(pending) : null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-history-modal">
        <div className="p-6 pb-4 border-b border-brand-soft flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-brand-lime/30 flex items-center justify-center shrink-0"><FileSpreadsheet className="w-5 h-5 text-brand-greenDeep" /></span>
          <div className="flex-1">
            <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Import de l'historique des revenus et des dépenses</h3>
            <p className="text-xs text-brand-green/50">Colonnes : Date · Bien · Locataire · Type · Catégorie · Description · Montant · Statut.</p>
          </div>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep" data-testid="import-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {result ? (
            <ResultStep result={result} onClose={onClose} onBack={() => setResult(null)} />
          ) : pending ? (
            <div className="space-y-5" data-testid="import-recap">
              <div>
                <h4 className="font-display text-lg font-semibold text-brand-greenDeep">Récapitulatif avant import</h4>
                <p className="text-sm text-brand-green/60">Vérifiez le résumé, puis confirmez pour générer les écritures.</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="rounded-2xl border border-brand-soft p-4 text-center">
                  <p className="text-xs text-brand-green/50 mb-1">Lignes détectées</p>
                  <p className="text-2xl font-bold text-brand-greenDeep" data-testid="recap-total-lines">{pending.length}</p>
                </div>
                <div className="rounded-2xl border border-green-200 bg-green-50/50 p-4 text-center">
                  <p className="text-xs text-green-700/70 mb-1">Revenus ({recap.nbRev})</p>
                  <p className="text-2xl font-bold text-green-700" data-testid="recap-revenus">+{recap.totRev.toLocaleString("fr-FR")} €</p>
                </div>
                <div className="rounded-2xl border border-orange-200 bg-orange-50/50 p-4 text-center">
                  <p className="text-xs text-orange-700/70 mb-1">Dépenses ({recap.nbDep})</p>
                  <p className="text-2xl font-bold text-orange-600" data-testid="recap-depenses">-{recap.totDep.toLocaleString("fr-FR")} €</p>
                </div>
              </div>
              <div className="rounded-2xl border border-brand-soft p-3 flex items-center justify-between">
                <span className="text-sm text-brand-green/60">Résultat net estimé</span>
                <span className={`text-lg font-bold ${recap.totRev - recap.totDep >= 0 ? "text-brand-greenDeep" : "text-orange-600"}`} data-testid="recap-net">{(recap.totRev - recap.totDep >= 0 ? "+" : "") + (recap.totRev - recap.totDep).toLocaleString("fr-FR")} €</span>
              </div>
              <div className="max-h-52 overflow-y-auto rounded-2xl border border-brand-soft">
                <table className="w-full text-xs">
                  <thead className="bg-brand-softer/60 text-brand-green/60 sticky top-0"><tr>
                    <th className="px-2 py-2 text-left font-semibold">Date</th>
                    <th className="px-2 py-2 text-left font-semibold">Bien</th>
                    <th className="px-2 py-2 text-left font-semibold">Locataire</th>
                    <th className="px-2 py-2 text-left font-semibold">Catégorie</th>
                    <th className="px-2 py-2 text-right font-semibold">Montant</th>
                  </tr></thead>
                  <tbody>
                    {pending.map((r, i) => (
                      <tr key={i} className="border-t border-brand-soft" data-testid={`recap-row-${i}`}>
                        <td className="px-2 py-1.5 whitespace-nowrap">{r.date}</td>
                        <td className="px-2 py-1.5 truncate max-w-[120px]">{r.property || "—"}</td>
                        <td className="px-2 py-1.5 truncate max-w-[120px]">{r.tenant || "—"}</td>
                        <td className="px-2 py-1.5 truncate max-w-[160px]">{r.type || "—"}</td>
                        <td className={`px-2 py-1.5 text-right font-semibold whitespace-nowrap ${rowSens(r) === "Revenu" ? "text-green-700" : "text-orange-600"}`}>{rowSens(r) === "Revenu" ? "+" : "-"}{Math.abs(parseNum(r.montant) || 0).toLocaleString("fr-FR")} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setPending(null)} disabled={busy} className="px-5 py-2.5 rounded-full border border-brand-soft text-brand-greenDeep font-semibold text-sm hover:bg-brand-softer disabled:opacity-50" data-testid="recap-cancel-btn">Annuler</button>
                <button onClick={confirmImport} disabled={busy} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm disabled:opacity-50" data-testid="recap-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirmer l'import ({pending.length})
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="rounded-2xl bg-brand-softer/40 border border-brand-soft p-4 text-sm text-brand-green/70 space-y-1.5">
                <p className="font-semibold text-brand-greenDeep">Comment ça marche ?</p>
                <p>1. Téléchargez le modèle Excel (revenus &amp; dépenses).</p>
                <p>2. Remplissez une ligne par écriture — colonne <b>Type</b> = « Revenu » ou « Dépense » (sinon déduit du signe du montant).</p>
                <p>3. Sélectionnez le fichier rempli : les écritures sont générées automatiquement dans « Dernières transactions ».</p>
              </div>
              <div className="rounded-2xl border-2 border-dashed border-brand-soft p-8 flex flex-col items-center text-center gap-4">
                {busy ? (
                  <div className="flex items-center gap-2 text-brand-green/70 py-4" data-testid="import-busy"><Loader2 className="w-5 h-5 animate-spin" /> Import en cours…</div>
                ) : (
                  <>
                    <span className="w-14 h-14 rounded-full bg-brand-lime/25 flex items-center justify-center"><FileSpreadsheet className="w-7 h-7 text-brand-greenDeep" /></span>
                    <div>
                      <p className="text-sm font-semibold text-brand-greenDeep">Modèle de revenus &amp; dépenses (.xlsx)</p>
                      <p className="text-xs text-brand-green/50">Colonnes : Date · Bien · Locataire · Type · Catégorie · Description · Montant · Statut</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/finances/import-history/template`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold text-sm" data-testid="import-template-btn"><Download className="w-4 h-4" /> Télécharger le modèle</a>
                      <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm" data-testid="import-excel-btn"><FileSpreadsheet className="w-4 h-4" /> Choisir un fichier rempli</button>
                    </div>
                  </>
                )}
                <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onExcel} data-testid="import-excel-input" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultStep({ result, onClose, onBack }) {
  const { created_count, errors = [], duplicates = [], recurring_candidates = [], loan_candidates = [], rent_candidates = [] } = result;
  const [doneRec, setDoneRec] = useState([]);

  const createRecurring = async (c, idx) => {
    try {
      await api.post("/recurring-expenses", {
        category: c.category, amount: c.amount, label: c.label || c.category,
        frequency: "Mensuelle", start_date: c.start_date, active: true,
      });
      setDoneRec((p) => [...p, idx]);
      toast.success(`Charge récurrente créée : ${c.category}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
  };

  return (
    <div className="space-y-5" data-testid="import-result">
      <div className="rounded-2xl bg-brand-lime/15 border border-brand-lime/40 p-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center"><Check className="w-5 h-5 text-brand-greenDeep" /></span>
        <div>
          <p className="font-semibold text-brand-greenDeep">{created_count} écriture(s) importée(s)</p>
          <p className="text-xs text-brand-green/60">Elles apparaissent maintenant dans « Dernières transactions ».</p>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50/50 p-4" data-testid="import-errors">
          <p className="text-sm font-semibold text-red-600 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" /> {errors.length} ligne(s) ignorée(s) (informations manquantes)</p>
          <ul className="text-xs text-red-600/90 space-y-0.5">
            {errors.slice(0, 8).map((e, i) => <li key={i}>Ligne {e.line} : {e.missing.join(", ")} manquant(s)</li>)}
          </ul>
        </div>
      )}

      {duplicates.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4" data-testid="import-duplicates">
          <p className="text-sm font-semibold text-amber-700 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" /> {duplicates.length} doublon(s) ignoré(s) (lignes strictement identiques : toutes les colonnes)</p>
          <ul className="text-xs text-amber-700/90 space-y-0.5">
            {duplicates.slice(0, 6).map((e, i) => <li key={i}>Ligne {e.line} : {e.date} · {e.type} · {e.description || "(sans description)"}{e.property ? ` · ${e.property}` : ""}</li>)}
          </ul>
        </div>
      )}

      {rent_candidates.length > 0 && (
        <div>
          <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2 mb-2"><Repeat className="w-4 h-4 text-brand-limeDark" /> Loyers récurrents détectés</h4>
          <div className="space-y-2">
            {rent_candidates.map((c, i) => <RentActions key={i} c={c} idx={i} />)}
          </div>
          <p className="text-[11px] text-brand-green/45 mt-1.5">Créés avec des données minimales — enrichissez-les ensuite manuellement ou via l'upload de documents (OCR).</p>
        </div>
      )}

      {recurring_candidates.length > 0 && (
        <div>
          <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2 mb-2"><Repeat className="w-4 h-4 text-brand-limeDark" /> Dépenses récurrentes détectées</h4>
          <div className="space-y-2">
            {recurring_candidates.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-brand-soft" data-testid={`recurring-candidate-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-brand-greenDeep truncate">{c.category}{c.de_a ? ` · ${c.de_a}` : ""}</p>
                  <p className="text-xs text-brand-green/50">{c.count} occurrences · ~{c.amount.toLocaleString("fr-FR")} € / mois</p>
                </div>
                {doneRec.includes(i)
                  ? <span className="text-xs font-semibold text-brand-greenDeep flex items-center gap-1"><Check className="w-4 h-4" /> Créée</span>
                  : <button onClick={() => createRecurring(c, i)} className="px-3 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-xs font-semibold whitespace-nowrap" data-testid={`create-recurring-${i}`}>Créer récurrente</button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {loan_candidates.length > 0 && (
        <div>
          <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2 mb-2"><Landmark className="w-4 h-4 text-brand-limeDark" /> Remboursements de prêt détectés</h4>
          <div className="space-y-3">
            {loan_candidates.map((c, i) => <LoanCandidate key={i} c={c} idx={i} />)}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onBack} className="px-5 py-2.5 rounded-full border border-brand-soft text-brand-greenDeep font-semibold text-sm hover:bg-brand-softer" data-testid="import-result-back">Importer d'autres</button>
        <button onClick={onClose} className="px-6 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm" data-testid="import-result-done">Terminer</button>
      </div>
    </div>
  );
}

function RentActions({ c, idx }) {
  const [step, setStep] = useState("create"); // create → generate → select → done
  const [leaseId, setLeaseId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [months, setMonths] = useState([]); // {id, month}
  const [sel, setSel] = useState([]);

  const doCreate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finances/import-history/create-rent", { de_a: c.de_a, property: c.property, amount: c.amount, start_date: c.start_date });
      setLeaseId(data.lease_id);
      setStep("generate");
      toast.success(`Locataire, bien et bail créés pour ${c.de_a} (à enrichir)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
    finally { setBusy(false); }
  };
  const doGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leases/${leaseId}/sync`);
      const all = await api.get("/receipts");
      const pend = (all.data || []).filter((r) => r.lease_id === leaseId && r.status !== "Payé");
      setMonths(pend.map((r) => ({ id: r.id, month: r.month })));
      setSel(pend.map((r) => r.id));
      setStep("select");
      toast.success(`${data.receipts_generated} quittance(s) générée(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); }
    finally { setBusy(false); }
  };
  const markSel = async () => {
    if (!sel.length) return toast.error("Sélectionnez au moins un mois");
    setBusy(true);
    try {
      const { data } = await api.post("/receipts/bulk-pay", { ids: sel });
      setStep("done");
      toast.success(`${data.paid} quittance(s) marquée(s) payée(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
    finally { setBusy(false); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="p-3 rounded-xl border border-brand-soft" data-testid={`rent-candidate-${idx}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-brand-greenDeep truncate">{c.de_a}{c.property ? ` · ${c.property}` : ""}</p>
          <p className="text-xs text-brand-green/50">{c.count} loyers · ~{c.amount.toLocaleString("fr-FR")} € / mois{step === "create" ? ` · à créer : ${c.missing.join(", ")}` : ""}</p>
        </div>
        {step === "create" && <button onClick={doCreate} disabled={busy} className="px-3 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-xs font-semibold whitespace-nowrap disabled:opacity-50" data-testid={`create-rent-${idx}`}>Créer locataire, bien &amp; bail</button>}
        {step === "generate" && <button onClick={doGenerate} disabled={busy} className="px-3 py-1.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-xs font-semibold whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5" data-testid={`gen-receipts-${idx}`}>{busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Générer les quittances</button>}
        {step === "done" && <span className="text-xs font-semibold text-brand-greenDeep flex items-center gap-1"><Check className="w-4 h-4" /> Terminé</span>}
      </div>
      {step === "select" && (
        <div className="mt-3 border-t border-brand-soft pt-3" data-testid={`month-picker-${idx}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-brand-green/70">Cochez les mois à marquer payés ({sel.length}/{months.length})</p>
            <button onClick={() => setSel(sel.length === months.length ? [] : months.map((m) => m.id))} className="text-[11px] text-brand-limeDark hover:text-brand-greenDeep font-semibold">{sel.length === months.length ? "Tout décocher" : "Tout cocher"}</button>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
            {months.map((m) => (
              <button key={m.id} onClick={() => toggle(m.id)} data-testid={`month-${idx}-${m.id}`} className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${sel.includes(m.id) ? "bg-brand-lime border-brand-lime text-brand-greenDeep font-semibold" : "bg-white border-brand-soft text-brand-green/60"}`}>{m.month}</button>
            ))}
            {months.length === 0 && <span className="text-xs text-brand-green/50">Aucune quittance en attente.</span>}
          </div>
          {months.length > 0 && (
            <button onClick={markSel} disabled={busy} className="mt-3 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-50" data-testid={`mark-paid-${idx}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Marquer payées la sélection</button>
          )}
        </div>
      )}
    </div>
  );
}

function LoanCandidate({ c, idx }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [rate, setRate] = useState("");
  const [months, setMonths] = useState("");
  const [start, setStart] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const create = async () => {
    if (!amount || !rate || !months || !start) return toast.error("Complétez montant, taux, durée et date de début");
    setBusy(true);
    try {
      const [y, m] = start.split("-");
      const params = { amount: Number(amount), months: Number(months), rate: Number(rate), insurance_rate: 0, defer_type: 1, defer_months: 0, start_year: Number(y), start_month: Number(m) };
      const calc = await api.post("/loans/calculate", params);
      await api.post("/loans", { source: "calcul", type: "Prêt : Immobilier", start_date: `${start}-01`, payer_to: c.de_a, description: c.label || c.category, params, schedule: calc.data.schedule });
      setDone(true);
      toast.success("Prêt créé avec son échéancier");
    } catch (e) { toast.error(e?.response?.data?.detail || "Création du prêt impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-3 rounded-xl border border-brand-soft" data-testid={`loan-candidate-${idx}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-brand-greenDeep truncate">{c.category}{c.de_a ? ` · ${c.de_a}` : ""}</p>
          <p className="text-xs text-brand-green/50">{c.count} échéance(s) · ~{c.monthly.toLocaleString("fr-FR")} € / mois · infos manquantes : {c.missing.join(", ")}</p>
        </div>
        {done
          ? <span className="text-xs font-semibold text-brand-greenDeep flex items-center gap-1"><Check className="w-4 h-4" /> Prêt créé</span>
          : <button onClick={() => setOpen((o) => !o)} className="px-3 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-xs font-semibold whitespace-nowrap" data-testid={`create-loan-${idx}`}>Créer le prêt</button>}
      </div>
      {open && !done && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="Montant emprunté €" className="px-2 py-1.5 rounded-lg border border-brand-soft text-xs outline-none focus:border-brand-limeDark" data-testid={`loan-amount-${idx}`} />
          <input value={rate} onChange={(e) => setRate(e.target.value)} type="number" step="0.01" placeholder="Taux %" className="px-2 py-1.5 rounded-lg border border-brand-soft text-xs outline-none focus:border-brand-limeDark" data-testid={`loan-rate-${idx}`} />
          <input value={months} onChange={(e) => setMonths(e.target.value)} type="number" placeholder="Durée (mois)" className="px-2 py-1.5 rounded-lg border border-brand-soft text-xs outline-none focus:border-brand-limeDark" data-testid={`loan-months-${idx}`} />
          <input value={start} onChange={(e) => setStart(e.target.value)} type="month" className="px-2 py-1.5 rounded-lg border border-brand-soft text-xs outline-none focus:border-brand-limeDark" data-testid={`loan-start-${idx}`} />
          <button onClick={create} disabled={busy} className="col-span-2 sm:col-span-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`loan-submit-${idx}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Landmark className="w-4 h-4" />} Créer le prêt et son échéancier
          </button>
        </div>
      )}
    </div>
  );
}
